/**
 * User profile page with persona selection
 */

import { Link } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import type { PersonaId } from "../../types/persona";
import { PersonaAvatar } from "../persona/PersonaAvatar";
import { PersonaSelector } from "../persona/PersonaSelector";

export function ProfilePage() {
  const { user } = useAuth();
  const {
    personas,
    currentPersonaId: currentPersona,
    isLoadingPersonas,
    isLoadingPreferences,
    updatePersona,
    isUpdatingPersona,
  } = useUserPreferences();

  const handlePersonaSelect = (personaId: PersonaId) => {
    updatePersona(personaId);
  };

  const selectedPersonaDetails = personas.find((p) => p.personaId === currentPersona);

  return (
    <div className="min-h-screen bg-surface-3">
      {/* Header */}
      <header className="bg-primary shadow-2">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-on-primary hover:opacity-80 transition-opacity"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            <span>Terug naar chat</span>
          </Link>
          <h1 className="text-xl font-bold text-on-primary">Profiel</h1>
          <div className="w-24" /> {/* Spacer for centering */}
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* User info card */}
        <div className="bg-surface-container-lowest rounded-lg shadow-3 p-6 mb-8">
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-primary-container flex items-center justify-center">
                <span className="text-2xl font-bold text-on-primary-container">
                  {user?.name?.charAt(0) ?? "?"}
                </span>
              </div>
            )}
            <div>
              <h2 className="text-xl font-bold text-on-surface">{user?.name}</h2>
              <p className="text-on-surface-variant">{user?.email}</p>
              {user?.isAdmin && (
                <span className="inline-block mt-1 px-2 py-0.5 bg-primary-container text-on-primary-container text-xs font-medium rounded-sm">
                  Beheerder
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Persona selection card */}
        <div className="bg-surface-container-lowest rounded-lg shadow-3 p-6">
          <h3 className="text-lg font-bold text-on-surface mb-2">🐺 Kies je gesprekspartner</h3>
          <p className="text-on-surface-variant mb-6">
            Selecteer het personage waar je mee wilt chatten. Elk personage heeft een eigen
            communicatiestijl.
          </p>

          {/* Current selection */}
          {selectedPersonaDetails && !isLoadingPreferences && (
            <div className="mb-6 p-4 bg-primary-container/40 rounded-md border border-primary/20">
              <div className="flex items-center gap-3">
                <PersonaAvatar persona={selectedPersonaDetails} size="md" />
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-on-primary-container">
                      {selectedPersonaDetails.emoji} {selectedPersonaDetails.name}
                    </h4>
                    <span className="text-xs bg-primary text-on-primary px-2 py-0.5 rounded-sm">
                      Actief
                    </span>
                  </div>
                  <p className="text-sm text-on-primary-container/80">
                    {selectedPersonaDetails.description}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Persona grid */}
          <PersonaSelector
            personas={personas}
            selectedId={currentPersona}
            onSelect={handlePersonaSelect}
            isLoading={isLoadingPersonas || isLoadingPreferences}
            showDescriptions={false}
          />

          {/* Saving indicator */}
          {isUpdatingPersona && (
            <div className="mt-4 flex items-center justify-center gap-2 text-primary">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <span>Opslaan...</span>
            </div>
          )}

          {/* Description of selected persona */}
          {selectedPersonaDetails && (
            <div className="mt-6 p-4 bg-surface-container rounded-md">
              <h4 className="font-medium text-on-surface mb-2">
                Over {selectedPersonaDetails.name}
              </h4>
              <p className="text-sm text-on-surface-variant">
                {selectedPersonaDetails.toneDescription}
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
